import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Check, X, Star, Timer, Trophy, Sparkles, RotateCcw, Home, CheckCircle2, Lightbulb, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { SpokenText, useReadAloud } from "../../context/ReadAloudContext";
import { ThemedShell, ThemedCard } from "../../components/child/ThemedShell";
import { EXERCISE_TYPES, MATH_EXERCISE_TYPES, DIFFICULTIES } from "../../lib/constants";
import { Button } from "../../components/ui/button";
import { sfx } from "../../lib/sound";
import { CountUp } from "../../components/common/CountUp";

function Confetti() {
  const colors = ["#3B82F6", "#F97316", "#FBBF24", "#22C55E", "#A855F7", "#00FFCC", "#FF3366"];
  return (
    <div className="pointer-events-none fixed inset-0 z-50">
      {[...Array(40)].map((_, i) => (
        <div key={i} className="confetti" style={{
          left: `${Math.random() * 100}%`, background: colors[i % colors.length],
          animationDelay: `${Math.random() * 0.8}s`, animationDuration: `${2 + Math.random() * 1.5}s`,
        }} />
      ))}
    </div>
  );
}

const TIMED = ["cito_toets", "reken_cito"];

function OrderQuestion({ items, value, onChange }) {
  const selected = value || [];
  const toggle = (it) => {
    sfx.select();
    if (selected.includes(it)) onChange(selected.filter((x) => x !== it));
    else onChange([...selected, it]);
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2.5">
        {items.map((it, j) => {
          const pos = selected.indexOf(it);
          const sel = pos !== -1;
          return (
            <button key={j} onClick={() => toggle(it)} data-testid={`order-item-${j}`}
              className="relative px-5 py-3 rounded-2xl font-heading font-extrabold text-lg border-2 transition-all active:scale-95"
              style={sel ? { background: "var(--t-primary)", color: "var(--t-on-primary)", borderColor: "var(--t-primary)" } : { background: "var(--t-surface2)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>
              {it}
              {sel && <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-black" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}>{pos + 1}</span>}
            </button>
          );
        })}
      </div>
      {selected.length > 0 && <button onClick={() => { sfx.click(); onChange([]); }} className="mt-3 text-sm font-bold underline" style={{ color: "var(--t-muted)" }} data-testid="order-reset">Wis volgorde</button>}
    </div>
  );
}

function MatchQuestion({ left, right, value, onChange }) {
  const map = value || {};
  const [sel, setSel] = useState(null);
  const usedRights = new Set(Object.values(map));
  const pickLeft = (i) => { sfx.select(); setSel(sel === i ? null : i); };
  const pickRight = (r) => {
    if (sel == null) return;
    sfx.select();
    const next = { ...map };
    Object.keys(next).forEach((k) => { if (next[k] === r) delete next[k]; });
    next[sel] = r;
    onChange(next);
    setSel(null);
  };
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-2.5">
        {left.map((l, i) => {
          const active = sel === i; const done = map[i] != null;
          return (
            <button key={i} onClick={() => pickLeft(i)} data-testid={`match-left-${i}`}
              className="w-full px-4 py-3 rounded-2xl font-heading font-bold border-2 text-left transition-all active:scale-[0.98]"
              style={active ? { background: "var(--t-accent)", color: "var(--t-on-accent)", borderColor: "var(--t-accent)" } : done ? { background: "color-mix(in srgb, var(--t-success) 18%, transparent)", color: "var(--t-text)", borderColor: "var(--t-success)" } : { background: "var(--t-surface2)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>
              <span className="flex items-center justify-between gap-2">{l}{done && <span className="text-sm font-extrabold" style={{ color: active ? "var(--t-on-accent)" : "var(--t-success)" }}>→ {map[i]}</span>}</span>
            </button>
          );
        })}
      </div>
      <div className="space-y-2.5">
        {right.map((r, j) => {
          const used = usedRights.has(r);
          return (
            <button key={j} onClick={() => pickRight(r)} disabled={sel == null} data-testid={`match-right-${j}`}
              className="w-full px-4 py-3 rounded-2xl font-heading font-bold border-2 transition-all active:scale-[0.98] disabled:opacity-60"
              style={used ? { background: "color-mix(in srgb, var(--t-primary) 16%, transparent)", color: "var(--t-text)", borderColor: "var(--t-primary)" } : { background: "var(--t-surface)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>
              {r}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ExercisePlayer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, terms } = useTheme();
  const { stop: stopReading } = useReadAloud();
  const cfg = location.state || { type: "begrijpend_lezen", subject: "lezen", theme: "Dieren" };

  const [exercise, setExercise] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loadMsg, setLoadMsg] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [explanations, setExplanations] = useState({});
  const [explLoading, setExplLoading] = useState(null);
  const isTimed = TIMED.includes(cfg.type);
  const timerRef = useRef(null);

  const loadExercise = async () => {
    stopReading?.();
    setExercise(null); setResult(null); setAnswers({}); setSeconds(0);
    try {
      const { data } = await api.post("/child/exercise/start", cfg);
      setExercise(data);
      if (isTimed) timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
      navigate("/leerling");
    }
  };

  useEffect(() => { loadExercise(); return () => clearInterval(timerRef.current); }, []);
  useEffect(() => {
    if (exercise) return;
    const t = setInterval(() => setLoadMsg((m) => (m + 1) % terms.loadingMsgs.length), 1800);
    return () => clearInterval(t);
  }, [exercise, terms.loadingMsgs.length]);

  const isAnswered = (i, q) => {
    const v = answers[i];
    if (q.answer_type === "input") return v != null && String(v).trim() !== "";
    if (q.answer_type === "order") return Array.isArray(v) && v.length === (q.items?.length || 0) && v.length > 0;
    if (q.answer_type === "match") return v != null && Object.keys(v).length === (q.left?.length || 0) && (q.left?.length || 0) > 0;
    return v != null && v !== "";
  };
  const answeredCount = exercise ? exercise.questions.filter((q, i) => isAnswered(i, q)).length : 0;

  const submit = async () => {
    if (answeredCount < exercise.questions.length) { toast.error("Beantwoord eerst alle vragen!"); return; }
    stopReading?.();
    clearInterval(timerRef.current);
    setSubmitting(true);
    try {
      const ans = exercise.questions.map((q, i) => {
        const v = answers[i];
        if (q.answer_type === "input") return String(v ?? "").trim();
        if (q.answer_type === "order") return Array.isArray(v) ? v.join("|") : "";
        if (q.answer_type === "match") return v ? Object.entries(v).map(([k, val]) => `${k}:${val}`).join("|") : "";
        return String(v ?? -1);
      });
      const { data } = await api.post("/child/exercise/submit", { exercise_id: exercise.id, answers: ans, duration: seconds });
      setResult(data);
      const pct = Math.round((data.score / data.total) * 100);
      if (pct >= 80) { sfx.win(); } else if (pct >= 50) { sfx.correct(); } else { sfx.wrong(); }
      if (data.coins_earned > 0) setTimeout(() => sfx.coin(), 450);
      if (data.new_badges?.length) setTimeout(() => sfx.badge(), 900);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const muted = { color: "var(--t-muted)" };

  const fetchExplain = async (i) => {
    if (explanations[i] || !exercise) return;
    setExplLoading(i);
    try {
      const { data } = await api.post("/child/explain", { exercise_id: exercise.id, question_index: i });
      setExplanations((e) => ({ ...e, [i]: data.steps }));
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setExplLoading(null);
    }
  };

  // Loading
  if (!exercise && !result) {
    return (
      <ThemedShell>
        <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
          <motion.div animate={{ y: [0, -14, 0], rotate: [0, -6, 6, 0] }} transition={{ repeat: Infinity, duration: 2.2 }} className="w-28 h-28 rounded-3xl flex items-center justify-center mb-6 text-6xl shadow-xl" style={{ background: "var(--t-surface2)" }}>{theme.mascot}</motion.div>
          <h1 className="font-heading text-2xl font-extrabold themed-heading">{terms.loading}</h1>
          <p className="mt-2 font-semibold" style={muted}>{terms.loadingMsgs[loadMsg]}</p>
          <div className="mt-6 flex gap-1.5">{[0, 1, 2].map((i) => <div key={i} className="w-3 h-3 rounded-full animate-bounce" style={{ background: "var(--t-accent)", animationDelay: `${i * 0.15}s` }} />)}</div>
        </div>
      </ThemedShell>
    );
  }

  // Results
  if (result) {
    const pct = Math.round((result.score / result.total) * 100);
    const great = pct >= 80;
    return (
      <ThemedShell>
        {great && <Confetti />}
        <div className="max-w-3xl mx-auto px-5 pt-12 pb-16">
          <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <ThemedCard className="p-8 text-center">
              <motion.div
                className="text-6xl mb-3"
                initial={{ scale: 0, rotate: -30 }}
                animate={great ? { scale: [0, 1.3, 1], rotate: [-30, 12, -8, 0], y: [0, -10, 0] } : { scale: 1, rotate: 0 }}
                transition={{ duration: great ? 0.9 : 0.5 }}
              >{theme.mascot}</motion.div>
              <h1 className="font-heading text-3xl font-black themed-heading">{great ? terms.results_great : pct >= 50 ? terms.results_good : terms.results_low}</h1>
              <p className="mt-2 font-semibold" style={muted}>Je had {result.score} van de {result.total} goed</p>

              <div className="my-6 relative w-36 h-36 mx-auto">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="var(--t-surface2)" strokeWidth="9" />
                  <motion.circle cx="50" cy="50" r="42" fill="none" stroke={great ? "var(--t-success)" : "var(--t-accent)"} strokeWidth="9" strokeLinecap="round"
                    strokeDasharray={264} initial={{ strokeDashoffset: 264 }} animate={{ strokeDashoffset: 264 - (264 * pct) / 100 }} transition={{ duration: 1, delay: 0.3 }} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center"><span className="font-heading text-3xl font-black themed-heading">{pct}%</span></div>
              </div>

              <div className="flex justify-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 px-4 py-2 rounded-2xl" style={{ background: "var(--t-surface2)" }} data-testid="result-xp"><Star className="w-5 h-5" style={{ color: "var(--t-accent)", fill: "var(--t-accent)" }} /><span className="font-heading font-extrabold">+<CountUp value={result.xp_earned} delay={300} /> {terms.xp}</span></div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-2xl" style={{ background: "var(--t-surface2)" }} data-testid="result-coins"><span className="text-lg">{terms.coinIcon}</span><span className="font-heading font-extrabold">+<CountUp value={result.coins_earned} delay={450} /> {terms.coins}</span></div>
                <div className="flex items-center gap-2 px-4 py-2 rounded-2xl" style={{ background: "var(--t-surface2)" }}><Trophy className="w-5 h-5" style={{ color: "var(--t-primary)" }} /><span className="font-heading font-extrabold">{terms.level} {result.level.level}</span></div>
              </div>

              {result.new_badges?.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-6 p-4 rounded-2xl" style={{ background: "var(--t-surface2)" }}>
                  <p className="font-heading font-bold flex items-center justify-center gap-2"><Sparkles className="w-5 h-5" style={{ color: "var(--t-accent)" }} /> Nieuwe {terms.badges.toLowerCase()}!</p>
                  <div className="flex justify-center gap-3 mt-3 flex-wrap">
                    {result.new_badges.map((b) => <span key={b.id} className="px-3 py-1.5 rounded-full font-bold text-sm" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }} data-testid={`new-badge-${b.id}`}>{b.title}</span>)}
                  </div>
                </motion.div>
              )}

              <div className="flex gap-3 mt-8">
                <Button onClick={loadExercise} className="flex-1 h-12 rounded-xl font-bold active:scale-95 border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }} data-testid="again-btn"><RotateCcw className="w-4 h-4 mr-1.5" /> {terms.again}</Button>
                <Button onClick={() => navigate("/leerling")} className="flex-1 h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }} data-testid="to-dashboard-btn"><Home className="w-4 h-4 mr-1.5" /> {terms.dashboard}</Button>
              </div>
            </ThemedCard>
          </motion.div>

          {/* Review */}
          <h2 className="font-heading text-xl font-extrabold mt-10 mb-4 themed-heading">Bekijk je antwoorden</h2>
          <div className="space-y-4">
            {result.results.map((r, i) => (
              <ThemedCard key={i} className="p-5" data-testid={`review-${i}`}>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: r.is_correct ? "var(--t-success)" : "var(--t-primary)", color: "#fff" }}>
                    {r.is_correct ? <Check className="w-4 h-4" strokeWidth={3} /> : <X className="w-4 h-4" strokeWidth={3} />}
                  </div>
                  <div className="flex-1">
                    <span className="text-xs font-bold uppercase tracking-wider" style={muted}>{r.skill_label}</span>
                    <p className="font-bold mt-0.5">{r.question}</p>
                    {r.answer_type !== "mc" ? (
                      <div className="mt-3 flex flex-wrap gap-2 text-sm font-semibold">
                        <span className="px-3 py-2 rounded-xl" style={{ background: r.is_correct ? "color-mix(in srgb, var(--t-success) 18%, transparent)" : "color-mix(in srgb, var(--t-primary) 18%, transparent)" }}>Jouw antwoord: {r.your_answer || "—"}</span>
                        {!r.is_correct && <span className="px-3 py-2 rounded-xl" style={{ background: "color-mix(in srgb, var(--t-success) 18%, transparent)" }}>Goed antwoord: {r.correct_answer}</span>}
                      </div>
                    ) : (
                      <div className="mt-3 space-y-1.5">
                        {r.options.map((o, j) => {
                          const isCorrect = j === r.correct_index;
                          const isYours = j === r.your_index;
                          const st = isCorrect ? { background: "color-mix(in srgb, var(--t-success) 18%, transparent)", color: "var(--t-text)" } : isYours ? { background: "color-mix(in srgb, var(--t-primary) 18%, transparent)", color: "var(--t-text)" } : { color: "var(--t-muted)" };
                          return (
                            <div key={j} className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold" style={st}>
                              {isCorrect ? <Check className="w-4 h-4" /> : isYours ? <X className="w-4 h-4" /> : <span className="w-4" />}{o}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <p className="mt-3 text-sm font-medium rounded-xl p-3" style={{ background: "var(--t-surface2)" }}>💡 {r.explanation}</p>
                    {!r.is_correct && (
                      <div className="mt-2">
                        {!explanations[i] ? (
                          <Button onClick={() => fetchExplain(i)} disabled={explLoading === i} data-testid={`explain-${i}-btn`}
                            className="rounded-full font-bold text-sm h-9 px-4 border-2 bg-transparent active:scale-95"
                            style={{ borderColor: "var(--t-accent)", color: "var(--t-accent)" }}>
                            {explLoading === i ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Lightbulb className="w-4 h-4 mr-1.5" />}
                            Laat zien hoe
                          </Button>
                        ) : (
                          <div className="rounded-xl p-3 border-2" style={{ background: "var(--t-surface2)", borderColor: "var(--t-accent)" }} data-testid={`explanation-${i}`}>
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="font-heading font-bold text-sm flex items-center gap-1.5"><Lightbulb className="w-4 h-4" style={{ color: "var(--t-accent)" }} /> Stap voor stap</span>
                            </div>
                            <SpokenText as="div" segKey={`expl-${i}`} text={explanations[i]} className="text-sm font-medium leading-relaxed whitespace-pre-line" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </ThemedCard>
            ))}
          </div>
        </div>
      </ThemedShell>
    );
  }

  // Exercise
  const typeMeta = EXERCISE_TYPES[exercise.type] || MATH_EXERCISE_TYPES[exercise.type];
  const hasPassage = !!(exercise.passage && exercise.passage.trim());

  const questionList = (
    <div className="space-y-5">
      {exercise.questions.map((q, i) => (
        <ThemedCard key={i} className="p-6" data-testid={`question-${i}`}>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-full font-heading font-bold text-sm flex items-center justify-center" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>{i + 1}</span>
            <span className="text-xs font-bold uppercase tracking-wider" style={muted}>{(q.skill || "").replace(/_/g, " ")}</span>
          </div>
          <SpokenText as="p" segKey={`q${i}`} text={q.question} className="font-bold text-lg mb-4" />
          {q.answer_type === "input" ? (
            <input type="text" inputMode="numeric" value={answers[i] ?? ""} onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value }))}
              placeholder="Typ je antwoord" data-testid={`q${i}-input`}
              className="w-full h-14 rounded-2xl px-4 text-lg font-bold outline-none border-2 focus:border-[color:var(--t-accent)]"
              style={{ background: "var(--t-surface2)", color: "var(--t-text)", borderColor: "var(--t-border)" }} />
          ) : q.answer_type === "order" ? (
            <OrderQuestion items={q.items || []} value={answers[i]} onChange={(v) => setAnswers((a) => ({ ...a, [i]: v }))} />
          ) : q.answer_type === "match" ? (
            <MatchQuestion left={q.left || []} right={q.right || []} value={answers[i]} onChange={(v) => setAnswers((a) => ({ ...a, [i]: v }))} />
          ) : (
            <div className="space-y-2.5">
              {q.options.map((o, j) => {
                const selected = answers[i] === j;
                return (
                  <button key={j} onClick={() => { sfx.select(); setAnswers((a) => ({ ...a, [i]: j })); }} data-testid={`q${i}-opt${j}`}
                    className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-left font-semibold transition-all active:scale-[0.98] border-2"
                    style={selected ? { background: "var(--t-primary)", color: "var(--t-on-primary)", borderColor: "var(--t-primary)" } : { background: "var(--t-surface)", color: "var(--t-text)", borderColor: "var(--t-border)" }}>
                    <span className="w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm shrink-0" style={selected ? { background: "var(--t-on-primary)", color: "var(--t-primary)" } : { background: "var(--t-surface2)", color: "var(--t-muted)" }}>{String.fromCharCode(65 + j)}</span>
                    <SpokenText segKey={`q${i}-o${j}`} text={o} />
                  </button>
                );
              })}
            </div>
          )}
        </ThemedCard>
      ))}
    </div>
  );

  return (
    <ThemedShell>
      <header className="sticky top-0 z-30 border-b backdrop-blur-md" style={{ background: `color-mix(in srgb, var(--t-surface) 75%, transparent)`, borderColor: "var(--t-border)" }}>
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <Button variant="ghost" onClick={() => { stopReading?.(); navigate("/leerling"); }} className="rounded-full font-bold" style={{ color: "var(--t-text)" }} data-testid="exit-exercise-btn"><ArrowLeft className="w-5 h-5 mr-1" /> Stoppen</Button>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline px-3 py-1.5 rounded-full font-bold text-sm" style={{ background: "var(--t-surface2)" }}>{typeMeta?.label} · {DIFFICULTIES[exercise.difficulty]}</span>
            {isTimed && <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full font-heading font-extrabold" style={{ background: "var(--t-surface2)", color: "var(--t-primary)" }} data-testid="timer"><Timer className="w-4 h-4" /> {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}</span>}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 pt-8 pb-28">
        {hasPassage ? (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="lg:sticky lg:top-24 lg:self-start">
              <ThemedCard className="p-7">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <SpokenText as="h1" segKey="title" text={exercise.title} className="font-heading text-2xl font-black themed-heading" />
                </div>
                <SpokenText as="p" segKey="passage" text={exercise.passage} className="leading-loose whitespace-pre-line" style={{ fontSize: "1.1rem" }} data-testid="passage" />
              </ThemedCard>
            </div>
            {questionList}
          </div>
        ) : (
          <div className="max-w-2xl mx-auto">
            <SpokenText as="h1" segKey="title" text={exercise.title} className="font-heading text-2xl font-black mb-5 themed-heading text-center block" data-testid="exercise-title" />
            {questionList}
          </div>
        )}
      </main>

      {/* Submit bar */}
      <div className="fixed bottom-0 inset-x-0 border-t z-30 backdrop-blur-md" style={{ background: `color-mix(in srgb, var(--t-surface) 88%, transparent)`, borderColor: "var(--t-border)" }}>
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between gap-4">
          <span className="font-bold text-sm" style={muted}>{answeredCount}/{exercise.questions.length} beantwoord</span>
          <Button onClick={submit} disabled={submitting} className="rounded-full font-bold text-lg py-6 px-10 active:scale-95 shadow-lg border-0" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }} data-testid="submit-exercise-btn">
            {submitting ? "Nakijken..." : <>Nakijken <CheckCircle2 className="w-5 h-5 ml-1.5" /></>}
          </Button>
        </div>
      </div>
    </ThemedShell>
  );
}
